import { PaymentIcon } from '@eventbrite/eds-payment-icon';
import { gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import React, { FunctionComponent, ReactNode } from 'react';
import {
    PAYMENT_CONSTRAINT_VARIANTS,
    PAYMENT_METHODS_INFO,
} from '../../../constants';
import {
    PaymentConstraintName,
    PaymentMethodType,
} from '../../../models/paymentMethods/enums';
import { formatConstraintText } from '../../../utils/tickets';

export interface TicketPaymentConstraintsProps {
    paymentConstraints: Array<{
        id: string;
        instrumentType: PaymentConstraintName;
        paymentMethod: PaymentConstraintName | null;
    }>;
}

const getIconForPaymentMethod = ({
    key,
    testidPrefix,
    icon,
}: {
    key?: string;
    testidPrefix: string;
    icon: ReactNode;
}) => (
    <span
        key={key}
        className="eds-l-mar-right-2"
        data-testid={`${testidPrefix}-payment-method-icon`}
    >
        <PaymentIcon type={icon} size="xsmall" />
    </span>
);

export const TicketPaymentConstraints: FunctionComponent<
    TicketPaymentConstraintsProps
> = ({ paymentConstraints }) => {
    if (!paymentConstraints.length) {
        return null;
    }

    const { paymentMethodIcons, paymentMethodTexts } =
        paymentConstraints.reduce<{
            paymentMethodTexts: Array<$FixMe>;
            paymentMethodIcons: Array<$FixMe>;
        }>(
            (accum, { id, instrumentType, paymentMethod }) => {
                const { icon, userString } = paymentMethod
                    ? PAYMENT_CONSTRAINT_VARIANTS[paymentMethod] || {}
                    : PAYMENT_CONSTRAINT_VARIANTS[instrumentType] || {};

                const constraintKey = paymentMethod || instrumentType;
                const text = userString || formatConstraintText(constraintKey);
                const iconContainer = icon
                    ? getIconForPaymentMethod({
                          key: id,
                          testidPrefix: constraintKey,
                          icon,
                      })
                    : null;

                return {
                    paymentMethodTexts: [...accum.paymentMethodTexts, text],
                    paymentMethodIcons: iconContainer
                        ? [...accum.paymentMethodIcons, iconContainer]
                        : accum.paymentMethodIcons,
                };
            },
            { paymentMethodTexts: [], paymentMethodIcons: [] },
        );

    const iconDisplay = paymentMethodIcons.length
        ? paymentMethodIcons
        : getIconForPaymentMethod({
              testidPrefix: PaymentMethodType.CREDIT,
              icon: PAYMENT_METHODS_INFO[PaymentMethodType.CREDIT].icon,
          });
    const commaSeparatedPaymentMethodNames = paymentMethodTexts.join(', ');
    const displayText =
        paymentMethodTexts.length === 1
            ? gettext('%(commaSeparatedPaymentMethodNames)s required', {
                  commaSeparatedPaymentMethodNames,
              })
            : gettext('%(commaSeparatedPaymentMethodNames)s accepted', {
                  commaSeparatedPaymentMethodNames,
              });

    return (
        <div
            className="eds-l-mar-vert-3"
            data-testid="ticket-payment-constraints"
        >
            {iconDisplay}
            <span className="eds-text-bs eds-text-color--ui-600">
                {displayText}
            </span>
        </div>
    );
};

export default TicketPaymentConstraints;
